import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Button, TextField } from "@mui/material";
import { PhoneInput } from "components/PhoneInput/PhoneInput";
import { AuthService } from "services/authService";
import whatsappService from "services/admin/whatsappService";
import { loginSuccessAction } from "redux/actions/authActions";
import { ADMIN_HOME_PATH } from "helpers/url";
import "../Login/login.scss";

/**
 * Демо-доступ: регистрация компании с пробным периодом (7 дней, срок задаёт тариф Trial).
 * Шаг 1 — данные компании и телефон, отправляем код в WhatsApp.
 * Шаг 2 — вводим код, создаём организацию с демо-данными и сразу входим.
 */
export const Register = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState({ name: "", orgName: "", phone: "", phonePrefix: 7, password: "" });
  const [code, setCode] = useState("");
  // Локальная разработка: бэкенд с WHATSAPP_OTP_DISABLED=true не отправляет код, а возвращает его
  const [devCode, setDevCode] = useState(null);
  const [step, setStep] = useState("details");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const getErrorMessage = (ex, fallback) =>
    ex?.response?.data?.error?.message || ex?.response?.data?.message || ex?.message || fallback;

  const sendCode = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.phone) {
      setError("Введите номер телефона");
      return;
    }
    if (form.password.length < 6) {
      setError("Пароль должен быть не короче 6 символов");
      return;
    }
    setLoading(true);
    try {
      const { data } = await whatsappService.singin(form.phone);
      if (data?.devCode) {
        setDevCode(data.devCode);
        setCode(data.devCode);
      }
      setStep("code");
    } catch (ex) {
      setError(getErrorMessage(ex, "Не удалось отправить код. Попробуйте позже"));
    } finally {
      setLoading(false);
    }
  };

  const signup = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await AuthService.trialSignup({
        name: form.name.trim(),
        orgName: form.orgName.trim(),
        phone: form.phone,
        password: form.password,
        code: code.trim(),
      });
      localStorage.setItem("orgID", data.orgID);
      dispatch(loginSuccessAction({ user: data.user, token: data.token }));
      navigate(ADMIN_HOME_PATH);
    } catch (ex) {
      setError(getErrorMessage(ex, "Не удалось зарегистрироваться. Попробуйте позже"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Демо-доступ | Ubee AI</title>
        <meta name="description" content="Попробуйте платформу Ubee AI бесплатно 7 дней" />
      </Helmet>
      <div className="login container login-form-wrapper content">
        <div className="firebase-sign-in">
          <h2 className="text-2xl font-bold mb-2">Попробовать бесплатно</h2>
          <p className="text-gray-500 mb-6">
            7 дней полного доступа, включая ИИ-агента. Мы заполним кабинет демо-данными: клиенты, лиды и обращения.
          </p>

          {step === "details" && (
            <form onSubmit={sendCode} className="flex flex-col gap-3 w-100">
              <TextField label="Ваше имя" value={form.name} onChange={setField("name")} required />
              <TextField label="Название компании" value={form.orgName} onChange={setField("orgName")} required />
              <div>
                <div className="text-sm text-gray-500 mb-1">Номер WhatsApp — пришлём код подтверждения</div>
                <PhoneInput
                  phoneNumber={form.phone}
                  countryCode={form.phonePrefix}
                  hideLabel={true}
                  onChange={(params) => {
                    setForm((prev) => ({
                      ...prev,
                      ...(params.countryCode ? { phonePrefix: params.countryCode } : {}),
                      ...(params.phone !== undefined ? { phone: params.phone } : {}),
                    }));
                  }}
                />
              </div>
              <TextField
                label="Пароль для входа"
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={setField("password")}
                helperText="Не короче 6 символов"
                required
              />
              {error && <div style={{ color: "red" }}>{error}</div>}
              <Button type="submit" variant="contained" disabled={loading} style={{ padding: "8px 0" }}>
                {loading ? "Отправляем код..." : "Получить код в WhatsApp"}
              </Button>
            </form>
          )}

          {step === "code" && (
            <form onSubmit={signup} className="flex flex-col gap-3 w-100">
              {devCode ? (
                <div className="text-orange-600">Dev-режим: код не отправлялся в WhatsApp и подставлен автоматически</div>
              ) : (
                <div>Мы отправили код в WhatsApp на номер +{form.phone}</div>
              )}
              <TextField
                label="Код подтверждения"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputProps={{ inputMode: "numeric", autoComplete: "one-time-code" }}
                autoFocus
                required
              />
              {error && <div style={{ color: "red" }}>{error}</div>}
              <Button type="submit" variant="contained" disabled={loading} style={{ padding: "8px 0" }}>
                {loading ? "Готовим кабинет..." : "Начать пробный период"}
              </Button>
              <Button variant="text" disabled={loading} onClick={() => { setStep("details"); setError(null); }}>
                Изменить данные
              </Button>
            </form>
          )}

          <div className="text-center mt-6 text-gray-500">
            Уже есть аккаунт? <Link to="/auth/login" className="text-blue-500">Войти</Link>
          </div>
        </div>
      </div>
    </>
  );
};
